// © Microsoft Corporation. All rights reserved.

import { ReactionToBehaviorTypeMap, addOrUpdateTelemetryObject, getHeadlineByReactionType, renderHybrid } from "../../Social.telemetry";
import { SocialBarPanelTelemetryObjects, socialBarPanelTelemetryActionDispatchers, socialBarPanelTelemetryContracts } from "./SocialBarPanel.telemetry";
import { chevron_down, comment, thumbs_up } from "../../utils/icons";

import AccessibilityHelpers from "../../utils/AccessibilityHelpers";
import MostReactions from "../most-reactions/MostReactions";
import React from "react";
import ReactionMenu from "../reaction-menu/ReactionMenu";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { getReactionColorClass } from "../../utils/styleHelpers";
import { renderEmojiByType } from "../../utils/emojiIcons";

export interface SocialBarPanelProps {
    managedClasses?: SocialClassNameContract;
    rating: string;
    onReaction: (type: string) => void;
    t: SocialLocalizedStrings;
    replyLabel?: JSX.Element;
    onCommentsClick: (target: HTMLElement) => void;
    onReactionsClick: (e: any, ref: HTMLElement) => void;
    mainTelemetryObject?: TelemetryObjectBase;
    showCommentsButton?: boolean;
    reactionMenuClassName?: string;
    responsiveClass?: string;
    reactionCounts?: number;
    label: string;
    theme?: string;
    reactionsDisabled?: boolean;
}

interface SocialBarPanelState {
    reactionMenuVisible: boolean;
    isHoverSocialBarPanel: boolean;
    miniReactionBreakdownVisible: boolean;
    isHoverMiniReactionBreakdown: boolean;

}

export default class SocialBarPanel extends React.Component<SocialBarPanelProps, SocialBarPanelState> {
    containerRef: React.RefObject<HTMLDivElement>;
    focusBackRef: HTMLElement;
    telemetryObjects: SocialBarPanelTelemetryObjects;
    reactionMenuTriggerRefs: Array<HTMLElement>;

    constructor(props) {
        super(props);

        this.state = {
            reactionMenuVisible: false,
            miniReactionBreakdownVisible: false,
            isHoverMiniReactionBreakdown: false,
            isHoverSocialBarPanel: true
        };

        this.containerRef = React.createRef<HTMLDivElement>();
        this.focusBackRef = null;
        this.reactionMenuTriggerRefs = [];
    }

    componentDidUpdate() {
        AccessibilityHelpers.attachAccessibilityListeners(document);
    }

    onLikeBtnClick(e, telemetryObject) {
    // Right Click Detection
        if ("which" in e && e.which == 3) { return; } else if ("button" in e && e.button == 2) { return; }

        const currentRating = this.props.rating || enums.reactions.like;
        this.props.onReaction(currentRating);

        this.setState({ reactionMenuVisible: false });
        socialBarPanelTelemetryActionDispatchers.likeButtonClick(e.currentTarget, telemetryObject);
    }

    onReactionMenuShow(target, isHover) {
        this.setState({ reactionMenuVisible: true, isHoverSocialBarPanel: isHover });
        socialBarPanelTelemetryActionDispatchers.likeButtonHover(target, this.telemetryObjects.likeButton);
    }

    onReactionMenuHide() {
        this.setState({reactionMenuVisible: false, isHoverSocialBarPanel: true});
    }

    reactionHandler(type) {
        this.props.onReaction(type);
    }

    onReactionsClick(e, ref, telemetryObject) {
        socialBarPanelTelemetryActionDispatchers.likeButtonClick(e.currentTarget, telemetryObject);
        this.setState({miniReactionBreakdownVisible: false, isHoverMiniReactionBreakdown: false});
        this.props.onReactionsClick(e, ref);
    }

    onReplyClick(e, telemetryObject) {
        document.dispatchEvent(new CustomEvent("cwt-comments-clicked"));
        socialBarPanelTelemetryActionDispatchers.commentsButtonClick(e.currentTarget, telemetryObject);
        this.props.onCommentsClick(e.currentTarget);
    }

    render() {
        const {t, rating, replyLabel, managedClasses, showCommentsButton, reactionMenuClassName, responsiveClass, reactionCounts, label, mainTelemetryObject, theme, reactionsDisabled} = this.props;
        const {reactionMenuVisible, isHoverSocialBarPanel} = this.state;

        this.generateOrUpdateTelemetryObjects();

        return (
            <div
                className={classNames(managedClasses.socialBarPanelContainer, responsiveClass)}
                ref={this.containerRef}
            >
                { !reactionsDisabled &&
                    <React.Fragment>
                        { this.reactionMenuTriggerRefs.length > 0 &&
                            <ReactionMenu
                                className={reactionMenuClassName}
                                visible={reactionMenuVisible}
                                onReaction={this.reactionHandler.bind(this)}
                                onHide={() => this.onReactionMenuHide()}
                                onShow={(target, isHover) => this.onReactionMenuShow(target, isHover)}
                                isHover={isHoverSocialBarPanel}
                                triggerElements={ this.reactionMenuTriggerRefs}
                                {...{managedClasses, mainTelemetryObject, t, rating}}
                            />
                        }
                        <i
                            className={classNames("cwt-link cwt-icon cwt-capitalize no-select", ["cwt-hide", rating && true])}
                            role="button"
                            onClick={(e) => !ReactionMenu.touchEnabled && this.onLikeBtnClick(e, this.telemetryObjects && this.telemetryObjects.likeButton)}
                            data-has-over={reactionMenuVisible}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.likeButton, false, el => this.reactionMenuTriggerRefs[0] = el)}
                        >
                            {thumbs_up}
                            <span className={classNames("cwt-icon-label", getReactionColorClass(rating))}>{t.EmojiLabellike}</span>
                        </i>
                        <i
                            key={`${mainTelemetryObject && mainTelemetryObject.contract.content.id}-${rating || ""}`}
                            className={classNames("cwt-link cwt-icon cwt-capitalize no-select", ["cwt-hide", !rating && true])}
                            role="button"
                            onClick={(e) => !ReactionMenu.touchEnabled && this.onLikeBtnClick(e, this.telemetryObjects && this.telemetryObjects.activeReactionButton)}
                            data-has-over={reactionMenuVisible}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.activeReactionButton, false, el => this.reactionMenuTriggerRefs[1] = el)}
                        >
                            {renderEmojiByType(rating)}
                            <span className={classNames("cwt-icon-label", getReactionColorClass(rating))}>{(rating ? t[`EmojiLabel${rating}`] : "")}</span>
                        </i>
                        <i className="cwt-link cwt-visible-on-focus mr-12" role="button" aria-label={t.AriaLabelsOpenReactionPanel} onClick={() => this.setState({reactionMenuVisible: true, isHoverSocialBarPanel: false})}> {chevron_down} </i>
                    </React.Fragment>
                }

                {showCommentsButton &&
                    <i className="cwt-link cwt-reply-btn cwt-icon" role="button" aria-label={t.AriaLabelsOpenReplies} onClick={e => this.onReplyClick(e, this.telemetryObjects && this.telemetryObjects.commentsButton)}
                        {...renderHybrid(this.telemetryObjects && this.telemetryObjects.commentsButton, false)}
                    >
                        {comment}
                        <span className="cwt-icon-label">{replyLabel || t.CommentReplyBtn}</span>
                    </i>
                }

                { !reactionsDisabled && <MostReactions
                    containerClassName="cwt-reaction-counts"
                    onReactionsClick={(e) => this.onReactionsClick(e, this.focusBackRef, this.telemetryObjects && this.telemetryObjects.reactionsCount)}
                    hasSeperatorBefore={true}
                    telemetryObject={this.telemetryObjects && this.telemetryObjects.reactionsCount}
                    customLabel={label}
                    {...{ reactionCounts, t, managedClasses, theme}}
                /> }
            </div>
        );
    }

    generateOrUpdateTelemetryObjects() {
        const { mainTelemetryObject, rating } = this.props;

        if (mainTelemetryObject) {
            const id = mainTelemetryObject.contract.content.id;
            const headline = (rating && getHeadlineByReactionType(rating, true));
            const behavior = (rating && ReactionToBehaviorTypeMap[rating]);

            this.telemetryObjects = {
                reactionsCount: addOrUpdateTelemetryObject(mainTelemetryObject, {...socialBarPanelTelemetryContracts.reactionsCount}, { id }, this.telemetryObjects && this.telemetryObjects.reactionsCount),
                likeButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...socialBarPanelTelemetryContracts.likeButton}, { id }, this.telemetryObjects && this.telemetryObjects.likeButton),
                activeReactionButton: !rating && this.telemetryObjects ? this.telemetryObjects.activeReactionButton : addOrUpdateTelemetryObject(mainTelemetryObject, {...socialBarPanelTelemetryContracts.likeButton, behavior}, { id, headline }, this.telemetryObjects && this.telemetryObjects.activeReactionButton),
                commentsButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...socialBarPanelTelemetryContracts.commentsButton}, { id }, this.telemetryObjects && this.telemetryObjects.commentsButton)
            };
        }
    }
}